import {t} from "@lingui/core/macro";
import {css, StyleSheet} from "aphrodite";
import * as React from "react";
import {useEffect, useLayoutEffect, useRef} from "react";
import {useSlate} from "slate-react";

import {isKhanmigoEditing} from "@khan/guide-math-editor";

import {styles} from "./guide-cursor-styles.ts";

const CursorLineWithLabel = () => {
    // NOTE(john): We need to define this dynamically because we need to
    // reference the i18n module, which is not available at the top level.
    const afterTextStyles = StyleSheet.create({
        afterText: {
            ":after": {
                // We render this label in an :after pseudo-element to prevent it
                // from appearing in the document element's textContent.
                content: `'${t`Khanmigo`}'`,
            },
        },
    });

    return (
        <span
            role="none"
            className={css(styles.cursorLine)}
            data-remove-on-print
        >
            <span
                role="none"
                className={css(styles.cursorLabel, afterTextStyles.afterText)}
            />
        </span>
    );
};

interface GuideCursorProps {
    atEnd?: boolean;
    children: React.ReactNode;
}

export default function GuideCursor({
    atEnd = false,
    children,
}: GuideCursorProps): React.ReactElement {
    const editor = useSlate();
    const ref = useRef<HTMLElement | null>(null);

    useLayoutEffect(() => {
        if (ref.current && atEnd) {
            // NOTE(reid): This is a little hacky, but the simplest thing I
            // could find to do. When the guide cursor is at the end of an
            // empty block (e.g. immediately after starting a new block), it
            // inserts this zero-width string with a <br> element in it because
            // it wants to treat the element as empty, with a line break after.
            // But since we don't actually render this as empty, but instead
            // show our cursor line, the <br> messes up the alignment briefly.
            // To fix this, we just remove that <br> in this edge case.
            // https://github.com/ianstormtaylor/slate/blob/c8236ee112969fcab70ff4f159e1e4a18084ca6b/packages/slate-react/src/components/string.tsx#L34-L41
            const zeroWidthStringLineBreak = ref.current.querySelector(
                "[data-slate-zero-width] br",
            );
            if (zeroWidthStringLineBreak) {
                zeroWidthStringLineBreak.remove();
            }
        }
    }, [atEnd]);

    useEffect(() => {
        if (!ref.current || !atEnd || !isKhanmigoEditing(editor)) {
            // We only set up auto-scroll if we've rendered a cursor element,
            // if it's the _end_ cursor element (the one with the little
            // "Khanmigo" flag on it), and if Khanmigo is actively editing.
            return;
        }

        if (typeof window.IntersectionObserver === "undefined") {
            // Bail out if the browser/environment doesn't support this, which
            // may occur in tests (JSdom) or in very old browsers. We'll treat
            // auto-scroll as a nice-to-have.
            return;
        }

        const element = ref.current;

        // This sets up an observer to keep track of whether the cursor element
        // is currently visible (i.e. scrolled into view). If not, we'll get
        // false for `isIntersecting`, and we'll trigger a smooth-scroll to
        // keep it visible. Note that IntersectionObserver only runs its
        // callback when visibility _changes_ (goes from visible to not visible
        // or vice-versa).
        const io = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) {
                    element.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                        inline: "nearest",
                    });
                }
            },
            {threshold: 1},
        );

        io.observe(element);

        return () => io.unobserve(element);
    }, [atEnd, children, editor]);

    return (
        <mark className={css(styles.highlight)} ref={ref} data-guide-cursor>
            {children}
            {atEnd && <CursorLineWithLabel />}
        </mark>
    );
}
